import { AUDIO_INPUT_DEVICE_KEY } from '../constants/appRuntime';
import { proto as vibeProto } from '../proto/AsrProto';
import { proto as listenerProto } from '../proto/ListenerProto';

const OPUS_SAMPLE_RATE = 48_000;
const OPUS_FRAME_SAMPLES = 2_880;
const OGG_STREAM_SERIAL = 0x69327801;
const RECORDER_STOP_TIMEOUT_MSEC = 5_000;

export interface RecorderOpusChunk {
    audioChunk: Uint8Array;
    timestamp: number;
}

interface VoiceCapture {
    id: string;
    chunks: RecorderOpusChunk[];
    error: Error | null;
    resolve?: (audio: Uint8Array) => void;
    reject?: (error: Error) => void;
    stopTimer?: ReturnType<typeof setTimeout>;
}

let activeCapture: VoiceCapture | null = null;
let hooksAttached = false;
let nextCaptureId = 0;

export const assistantVoiceCapture = {
    isAvailable() {
        const api = window.electronAPI;
        return Boolean(
            api?.startAssistantRecording && api.stopAssistantRecording && api.onAssistantRecorderChunk && api.onAssistantRecorderStopped
        );
    },
    start() {
        const api = window.electronAPI;
        if (!this.isAvailable() || !api) throw new Error('Voice recorder is unavailable');
        if (activeCapture) throw new Error('Voice recording is already active');

        if (!hooksAttached) {
            api.onAssistantRecorderChunk?.(({ id, chunk }) => {
                if (activeCapture?.id !== id) return;
                try {
                    const decoded = vibeProto.AudioChunkWithMetadata.decode(chunk);
                    if (decoded.channel === listenerProto.DataFlow.CAPTURE) {
                        activeCapture.chunks.push({ audioChunk: new Uint8Array(decoded.audioChunk), timestamp: decoded.timestamp });
                    }
                } catch (error) {
                    activeCapture.error = error instanceof Error ? error : new Error(String(error));
                }
            });
            api.onAssistantRecorderStopped?.(({ id, reason }) => {
                const capture = activeCapture;
                if (capture?.id !== id) return;
                const error =
                    capture.error ||
                    (reason[0] !== 0 || reason[1] !== 'BY_APP'
                        ? new Error(String(reason[2] || reason[1] || 'Voice recorder stopped'))
                        : null);
                if (!capture.resolve) {
                    capture.error = error || new Error('Voice recorder stopped before release');
                    return;
                }
                if (capture.stopTimer) clearTimeout(capture.stopTimer);
                activeCapture = null;
                if (error) {
                    capture.reject?.(error);
                } else {
                    try {
                        capture.resolve?.(muxRecorderOpusToOgg(capture.chunks));
                    } catch (muxError) {
                        capture.reject?.(muxError instanceof Error ? muxError : new Error(String(muxError)));
                    }
                }
            });
            hooksAttached = true;
        }

        const id = `${Date.now()}-${++nextCaptureId}`;
        activeCapture = { id, chunks: [], error: null };
        const micDeviceName = localStorage.getItem(AUDIO_INPUT_DEVICE_KEY);
        api.startAssistantRecording?.({
            id,
            extraArgs: micDeviceName ? [`-record-mic-device-name=${micDeviceName.normalize('NFC')}`] : []
        });
    },
    stop() {
        const capture = activeCapture;
        if (!capture) return Promise.reject(new Error('Voice recording is not active'));
        if (capture.resolve) return Promise.reject(new Error('Voice recording is already stopping'));
        if (capture.error) {
            activeCapture = null;
            window.electronAPI?.stopAssistantRecording?.(capture.id);
            return Promise.reject(capture.error);
        }
        return new Promise<Uint8Array>((resolve, reject) => {
            capture.resolve = resolve;
            capture.reject = reject;
            capture.stopTimer = setTimeout(() => {
                if (activeCapture !== capture) return;
                activeCapture = null;
                reject(new Error('Voice recorder did not stop'));
            }, RECORDER_STOP_TIMEOUT_MSEC);
            window.electronAPI?.stopAssistantRecording?.(capture.id);
        });
    },
    cancel() {
        const capture = activeCapture;
        if (!capture) return;
        activeCapture = null;
        if (capture.stopTimer) clearTimeout(capture.stopTimer);
        capture.reject?.(new Error('Voice recording was cancelled'));
        window.electronAPI?.stopAssistantRecording?.(capture.id);
    }
};

const oggCrcTable = new Uint32Array(256);
for (let index = 0; index < oggCrcTable.length; index += 1) {
    let value = index << 24;
    for (let bit = 0; bit < 8; bit += 1) {
        value = value & 0x80000000 ? (value << 1) ^ 0x04c11db7 : value << 1;
    }
    oggCrcTable[index] = value >>> 0;
}

function oggPage(packet: Uint8Array, granulePosition: number, sequence: number, headerType: number) {
    const segmentCount = Math.floor(packet.length / 255) + 1;
    const page = new Uint8Array(27 + segmentCount + packet.length);
    const view = new DataView(page.buffer);

    page.set([0x4f, 0x67, 0x67, 0x53], 0);
    page[4] = 0;
    page[5] = headerType;
    view.setUint32(6, granulePosition >>> 0, true);
    view.setUint32(10, Math.floor(granulePosition / 0x1_0000_0000), true);
    view.setUint32(14, OGG_STREAM_SERIAL, true);
    view.setUint32(18, sequence, true);
    page[26] = segmentCount;

    let remaining = packet.length;
    for (let index = 0; index < segmentCount; index += 1) {
        const length = Math.min(remaining, 255);
        page[27 + index] = length;
        remaining -= length;
    }
    page.set(packet, 27 + segmentCount);

    let checksum = 0;
    for (const byte of page) {
        checksum = ((checksum << 8) ^ oggCrcTable[((checksum >>> 24) ^ byte) & 0xff]) >>> 0;
    }
    view.setUint32(22, checksum, true);

    return page;
}

export function muxRecorderOpusToOgg(chunks: RecorderOpusChunk[]) {
    const packets: { data: Uint8Array; timestamp: number }[] = [];

    for (const chunk of chunks) {
        let offset = 0;
        let frameIndex = 0;
        while (offset < chunk.audioChunk.length) {
            if (offset + 2 > chunk.audioChunk.length) throw new Error('Invalid recorder Opus payload');
            const length = chunk.audioChunk[offset] | (chunk.audioChunk[offset + 1] << 8);
            offset += 2;
            if (length === 0 || offset + length > chunk.audioChunk.length) throw new Error('Invalid recorder Opus payload');
            packets.push({
                data: chunk.audioChunk.slice(offset, offset + length),
                timestamp: chunk.timestamp + frameIndex * OPUS_FRAME_SAMPLES
            });
            offset += length;
            frameIndex += 1;
        }
    }

    if (packets.length === 0) throw new Error('No audio was recorded');

    const opusHead = new Uint8Array(19);
    opusHead.set([0x4f, 0x70, 0x75, 0x73, 0x48, 0x65, 0x61, 0x64]);
    opusHead[8] = 1;
    opusHead[9] = 1;
    new DataView(opusHead.buffer).setUint32(12, OPUS_SAMPLE_RATE, true);

    const vendor = Uint8Array.from('i2x-coach', character => character.charCodeAt(0));
    const opusTags = new Uint8Array(16 + vendor.length);
    opusTags.set([0x4f, 0x70, 0x75, 0x73, 0x54, 0x61, 0x67, 0x73]);
    new DataView(opusTags.buffer).setUint32(8, vendor.length, true);
    opusTags.set(vendor, 12);

    const pages = [oggPage(opusHead, 0, 0, 2), oggPage(opusTags, 0, 1, 0)];
    const firstTimestamp = packets[0].timestamp;
    packets.forEach((packet, index) => {
        pages.push(
            oggPage(
                packet.data,
                Math.max(OPUS_FRAME_SAMPLES, packet.timestamp - firstTimestamp + OPUS_FRAME_SAMPLES),
                index + 2,
                index === packets.length - 1 ? 4 : 0
            )
        );
    });

    const size = pages.reduce((total, page) => total + page.length, 0);
    const ogg = new Uint8Array(size);
    let offset = 0;
    pages.forEach(page => {
        ogg.set(page, offset);
        offset += page.length;
    });
    return ogg;
}
